import { useState } from 'react';
import { ChevronDown, X } from 'lucide-react';

import type { Season } from '@/types/season';

interface PrepSeasonSelectorProps {
  seasons: Season[];
  selectedSeasonId: string | null;
  onSelect: (id: string) => void;
}

/**
 * Always-visible season selector pill for the Prep tab header.
 *
 * - Single season  → non-interactive label pill (no chevron)
 * - Multi-season   → tappable pill with chevron-down → bottom sheet
 * - PRIMARY indicator shown only when 2+ seasons AND one is isPrimary
 */
export function PrepSeasonSelector({
  seasons,
  selectedSeasonId,
  onSelect,
}: PrepSeasonSelectorProps) {
  const [sheetOpen, setSheetOpen] = useState(false);

  const selectedSeason = seasons.find((s) => s.id === selectedSeasonId) ?? seasons[0];
  const isMulti = seasons.length > 1;
  const hasPrimary = isMulti && seasons.some((s) => s.isPrimary === true);

  function handleSelect(id: string) {
    onSelect(id);
    setSheetOpen(false);
  }

  const pillContent = (
    <>
      <span
        className="text-body-sm font-medium text-bark-100 truncate max-w-[180px]"
        data-testid="prep-season-selector-name"
      >
        {selectedSeason?.name ?? 'No season'}
      </span>
      {hasPrimary && selectedSeason?.isPrimary && (
        <span
          className="ml-1 flex-shrink-0 inline-flex items-center"
          data-testid="prep-season-selector-primary-dot"
          aria-label="Primary season"
        >
          <span
            className="w-1.5 h-1.5 rounded-full bg-[var(--color-accent)]"
          />
        </span>
      )}
      {isMulti && (
        <ChevronDown
          className="w-4 h-4 flex-shrink-0 text-bark-400"
          aria-hidden="true"
        />
      )}
    </>
  );

  return (
    <>
      {isMulti ? (
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          data-testid="prep-season-selector-pill"
          aria-label="Select season"
          aria-expanded={sheetOpen}
          className="inline-flex items-center gap-1.5 glass-cockpit rounded-full pl-3 pr-2.5 py-1.5 shadow-card-lg hover:opacity-90 transition-opacity duration-100"
        >
          {pillContent}
        </button>
      ) : (
        <div
          data-testid="prep-season-selector-pill"
          aria-label="Active season"
          className="inline-flex items-center gap-1.5 glass-cockpit rounded-full px-3 py-1.5 shadow-card-lg"
        >
          {pillContent}
        </div>
      )}

      {sheetOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/60"
            onClick={() => setSheetOpen(false)}
            aria-hidden="true"
            data-testid="prep-season-selector-backdrop"
          />

          {/* Bottom sheet */}
          <div
            className="fixed bottom-0 left-0 right-0 z-50 bg-[var(--color-elevated)] border-t border-[var(--color-border)] rounded-t-2xl shadow-card-lg flex flex-col max-h-[70vh]"
            role="dialog"
            aria-modal="true"
            aria-label="Select season"
            data-testid="prep-season-selector-sheet"
          >
            {/* Sheet header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <h2 className="font-display text-[18px] text-[var(--color-text-display)]">
                Select Season
              </h2>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                aria-label="Close season picker"
                className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
                data-testid="prep-season-selector-close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Season list */}
            <div className="overflow-y-auto pb-8" data-testid="prep-season-selector-list">
              {seasons.map((season) => {
                const isSelected = season.id === selectedSeasonId;
                const showPrimaryDot = hasPrimary && season.isPrimary === true;

                return (
                  <button
                    key={season.id}
                    type="button"
                    onClick={() => handleSelect(season.id)}
                    data-testid={`prep-season-option-${season.id}`}
                    aria-pressed={isSelected}
                    className="w-full flex items-center justify-between px-5 py-3.5 border-b border-[var(--color-border)] text-left hover:bg-[var(--color-surface)] transition-colors duration-100"
                  >
                    <div className="flex items-center gap-2">
                      {showPrimaryDot && (
                        <span
                          className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-[var(--color-accent)]"
                          aria-hidden="true"
                        />
                      )}
                      <span
                        className={`text-body-sm ${isSelected ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-secondary)]'}`}
                      >
                        {season.name}
                      </span>
                    </div>
                    {isSelected && (
                      <span
                        className="text-[10px] field-label text-[var(--color-accent)]"
                        aria-hidden="true"
                      >
                        ACTIVE
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
}
